import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

interface HistoricalCurvePoint {
  moistureContent: number;
  tr: number;
}

interface HistoricalCurveCard {
  title: string;
  average: boolean;
  points: HistoricalCurvePoint[];
}

@Component({
    selector: 'app-historical-curve-points',
    templateUrl: './historical-curve-points.component.html',
    styleUrls: ['./historical-curve-points.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HistoricalCurvePointsComponent implements OnInit {
  sampleLabel = '';
  trHeader = '';
  cards: HistoricalCurveCard[] = [];
  hasHistoricalCurves = false;

  constructor(public activatedRoute: ActivatedRoute) {}

  ngOnInit(): void {
    const query = this.activatedRoute.snapshot.queryParamMap;
    this.sampleLabel = query.get('label') ?? '';
    this.trHeader = query.get('metric') === 'true' ? 'TR (K.m/W)' : 'TR (°C-cm/W)';
    const averageCurve = HistoricalCurvePointsComponent.parseCurve(query.get('average'));
    const historicalCurves = HistoricalCurvePointsComponent.parseCurves(query.get('historical'));
    this.hasHistoricalCurves = historicalCurves.length > 0;
    this.cards = [
      {
        title: 'Average',
        average: true,
        points: averageCurve,
      },
      ...historicalCurves.map((points, index) => ({
        title: `Historical ${index + 1}`,
        average: false,
        points,
      })),
    ];
  }

  private static parseJson(value: string | null): unknown {
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  private static toPoints(value: unknown): HistoricalCurvePoint[] {
    if (!Array.isArray(value)) return [];
    return value
      .filter((tuple) => Array.isArray(tuple) && tuple.length === 2)
      .map((tuple) => ({
        moistureContent: Number(tuple[0]),
        tr: Number(tuple[1]),
      }));
  }

  private static parseCurve(value: string | null): HistoricalCurvePoint[] {
    return HistoricalCurvePointsComponent.toPoints(HistoricalCurvePointsComponent.parseJson(value));
  }

  private static parseCurves(value: string | null): HistoricalCurvePoint[][] {
    const parsed = HistoricalCurvePointsComponent.parseJson(value);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((curve) => HistoricalCurvePointsComponent.toPoints(curve));
  }
}
